/**
 * CBR Calculation Service
 *
 * Centralizes California Bearing Ratio (CBR) to Resilient Modulus (Mr) conversion calculations.
 * Eliminates duplicated formulas across the codebase and provides single source of truth
 * for AASHTO 1993 CBR-Mr correlations.
 *
 * Original Issue: CBR formula was duplicated in 9+ files with magic numbers scattered throughout.
 * Solution: Centralized service using AASHTOConstants for all coefficients.
 *
 * Excel References:
 * - Formula appears in multiple Excel sheets (DATABASE.txt, CALCULATION.txt)
 * - AASHTO 1993 Guide for Design of Pavement Structures
 *
 * @see AASHTOConstants.getCBRConstants() for coefficient values
 */

import { AASHTOConstants } from './AASHTOConstants';

/**
 * CBR Calculation Service
 *
 * Provides AASHTO 1993 compliant CBR to Resilient Modulus conversions.
 * All magic numbers are eliminated - constants loaded from AASHTOConstants.
 */
export class CBRCalculationService {
    /**
     * Calculate Resilient Modulus in PSI from CBR value
     *
     * Formula:
     * - For CBR <= 5:  Mr(MPa) = CBR * 10,  then convert to PSI
     * - For CBR > 5:   Mr(MPa) = 17.621 * CBR^0.64,  then convert to PSI
     *
     * Conversion: Mr(PSI) = Mr(kPa) * 0.145  (or Mr(MPa) * 1000 * 0.145)
     *
     * @param cbr - California Bearing Ratio (dimensionless, typically 0-100)
     * @returns Resilient Modulus in PSI
     *
     * @example
     * ```typescript
     * const mr = CBRCalculationService.calculateMrInPsi(3);   // Low CBR: 3 * 10 = 30 MPa = 4350 PSI
     * const mr = CBRCalculationService.calculateMrInPsi(10);  // High CBR: 17.621 * 10^0.64 ≈ 88.1 MPa
     * ```
     */
    public static calculateMrInPsi(cbr: number): number {
        if (cbr <= 0) return 0;

        const constants = AASHTOConstants.getCBRConstants();
        const {
            THRESHOLD_CBR,
            MULTIPLIER_LOW_CBR,
            COEFFICIENT_HIGH_CBR,
            EXPONENT_HIGH_CBR,
            KPA_TO_PSI,
            KPA_MULTIPLIER,
        } = constants;

        // Calculate Mr in MPa
        const mrMPa =
            cbr <= THRESHOLD_CBR
                ? cbr * MULTIPLIER_LOW_CBR
                : COEFFICIENT_HIGH_CBR * Math.pow(cbr, EXPONENT_HIGH_CBR);

        // Convert MPa → kPa → PSI
        const mrKPa = mrMPa * KPA_MULTIPLIER;
        const mrPsi = mrKPa * KPA_TO_PSI;

        return mrPsi;
    }

    /**
     * Calculate Resilient Modulus in kPa from CBR value
     *
     * Same formula as calculateMrInPsi but returns result in kPa.
     *
     * @param cbr - California Bearing Ratio (dimensionless, typically 0-100)
     * @returns Resilient Modulus in kPa
     *
     * @example
     * ```typescript
     * const mr = CBRCalculationService.calculateMrInKPa(3);   // 30 MPa = 30000 kPa
     * const mr = CBRCalculationService.calculateMrInKPa(10);  // ≈ 88100 kPa
     * ```
     */
    public static calculateMrInKPa(cbr: number): number {
        if (cbr <= 0) return 0;

        const constants = AASHTOConstants.getCBRConstants();
        const {
            THRESHOLD_CBR,
            MULTIPLIER_LOW_CBR,
            COEFFICIENT_HIGH_CBR,
            EXPONENT_HIGH_CBR,
            KPA_MULTIPLIER,
        } = constants;

        // Calculate Mr in MPa
        const mrMPa =
            cbr <= THRESHOLD_CBR
                ? cbr * MULTIPLIER_LOW_CBR
                : COEFFICIENT_HIGH_CBR * Math.pow(cbr, EXPONENT_HIGH_CBR);

        // Convert MPa → kPa
        const mrKPa = mrMPa * KPA_MULTIPLIER;

        return mrKPa;
    }

    /**
     * Calculate Resilient Modulus in MPa from CBR value
     *
     * Returns Mr in base unit (MPa) without conversion.
     *
     * @param cbr - California Bearing Ratio (dimensionless, typically 0-100)
     * @returns Resilient Modulus in MPa
     *
     * @example
     * ```typescript
     * const mr = CBRCalculationService.calculateMrInMPa(3);   // 30 MPa
     * const mr = CBRCalculationService.calculateMrInMPa(10);  // ≈ 88.1 MPa
     * ```
     */
    public static calculateMrInMPa(cbr: number): number {
        if (cbr <= 0) return 0;

        const constants = AASHTOConstants.getCBRConstants();
        const { THRESHOLD_CBR, MULTIPLIER_LOW_CBR, COEFFICIENT_HIGH_CBR, EXPONENT_HIGH_CBR } = constants;

        // Calculate Mr in MPa (base formula)
        const mrMPa =
            cbr <= THRESHOLD_CBR
                ? cbr * MULTIPLIER_LOW_CBR
                : COEFFICIENT_HIGH_CBR * Math.pow(cbr, EXPONENT_HIGH_CBR);

        return mrMPa;
    }

    /**
     * Check if CBR value uses low or high formula
     *
     * Utility method to determine which calculation branch is used.
     *
     * @param cbr - California Bearing Ratio
     * @returns true if low formula (CBR <= 5), false if high formula (CBR > 5)
     *
     * @example
     * ```typescript
     * CBRCalculationService.isLowCBR(3);   // true  (uses Mr = CBR * 10)
     * CBRCalculationService.isLowCBR(10);  // false (uses Mr = 17.621 * CBR^0.64)
     * ```
     */
    public static isLowCBR(cbr: number): boolean {
        const constants = AASHTOConstants.getCBRConstants();
        return cbr <= constants.THRESHOLD_CBR;
    }

    /**
     * Get the CBR threshold value that determines formula selection
     *
     * @returns Threshold CBR value (default: 5)
     */
    public static getThresholdCBR(): number {
        const constants = AASHTOConstants.getCBRConstants();
        return constants.THRESHOLD_CBR;
    }

    /**
     * Validate CBR input value
     *
     * @param cbr - CBR value to validate
     * @returns Validation result with error messages if invalid
     *
     * @example
     * ```typescript
     * const result = CBRCalculationService.validateCBR(-1);
     * // Returns: { isValid: false, errors: ['CBR must be non-negative'] }
     * ```
     */
    public static validateCBR(cbr: number): { isValid: boolean; errors: string[] } {
        const errors: string[] = [];

        if (cbr < 0) {
            errors.push('CBR must be non-negative');
        }

        if (cbr > 100) {
            errors.push('CBR value > 100 is unusual and may indicate data error');
        }

        if (!isFinite(cbr)) {
            errors.push('CBR must be a finite number');
        }

        return {
            isValid: errors.length === 0,
            errors,
        };
    }

    /**
     * Calculate Mr with validation
     *
     * Validates CBR input before calculation and returns error state if invalid.
     *
     * @param cbr - California Bearing Ratio
     * @param unit - Output unit ('psi' | 'kPa' | 'MPa')
     * @returns Object with mr value and validation status
     *
     * @example
     * ```typescript
     * const result = CBRCalculationService.calculateMrWithValidation(10, 'psi');
     * // Returns: { mr: 12775.5, isValid: true, errors: [] }
     *
     * const result = CBRCalculationService.calculateMrWithValidation(-5, 'psi');
     * // Returns: { mr: 0, isValid: false, errors: ['CBR must be non-negative'] }
     * ```
     */
    public static calculateMrWithValidation(
        cbr: number,
        unit: 'psi' | 'kPa' | 'MPa' = 'psi'
    ): {
        mr: number;
        isValid: boolean;
        errors: string[];
    } {
        const validation = this.validateCBR(cbr);

        if (!validation.isValid) {
            return {
                mr: 0,
                isValid: false,
                errors: validation.errors,
            };
        }

        let mr: number;
        switch (unit) {
            case 'kPa':
                mr = this.calculateMrInKPa(cbr);
                break;
            case 'MPa':
                mr = this.calculateMrInMPa(cbr);
                break;
            case 'psi':
            default:
                mr = this.calculateMrInPsi(cbr);
                break;
        }

        return {
            mr,
            isValid: true,
            errors: [],
        };
    }
}
